"use client";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { JURISDICTIONS, CATEGORIES } from "@/lib/constants";

export default function TrendsView({ allArticles, activeJur }) {
  const jurData = JURISDICTIONS.find((j) => j.id === activeJur);
  const jurColor = jurData?.color || "#FF5D31";
  const jurName = jurData?.name || activeJur;

  const articles = allArticles[activeJur] || [];

  // Category breakdown for this jurisdiction
  const catCounts = {};
  articles
    .filter((a) => a.classification?.relevant)
    .forEach((a) => {
      const c = a.classification.category;
      catCounts[c] = (catCounts[c] || 0) + 1;
    });
  const catData = Object.entries(catCounts)
    .map(([key, value]) => ({
      name: CATEGORIES[key]?.label || key,
      value,
      color: CATEGORIES[key]?.color || "#70706E",
    }))
    .sort((a, b) => b.value - a.value);

  // Articles by week
  const weekBuckets = {};
  articles
    .filter((a) => a.classification?.relevant)
    .forEach((a) => {
      const d = a.ingestedAt || a.date || "unknown";
      try {
        const date = new Date(d);
        if (!isNaN(date.getTime())) {
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - date.getDay());
          const key = weekStart.toISOString().slice(0, 10);
          weekBuckets[key] = (weekBuckets[key] || 0) + 1;
        }
      } catch {
        /* skip */
      }
    });
  const weekData = Object.entries(weekBuckets)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([week, count]) => ({ week: week.slice(5), count }));

  const totalStored = articles.length;
  const totalRelevant = articles.filter((a) => a.classification?.relevant).length;

  // Source breakdown
  const sourceCounts = {};
  articles
    .filter((a) => a.classification?.relevant)
    .forEach((a) => {
      sourceCounts[a.source] = (sourceCounts[a.source] || 0) + 1;
    });
  const sourceData = Object.entries(sourceCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Summary stats */}
      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
        {[
          { label: "Total stored", value: totalStored },
          { label: "Relevant", value: totalRelevant },
          { label: "Filtered out", value: totalStored - totalRelevant },
          { label: "Categories", value: Object.keys(catCounts).length },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              flex: "1 1 140px",
              padding: "16px",
              backgroundColor: "#f7f3ea",
              borderRadius: "4px",
              border: "1px solid #999",
            }}
          >
            <div
              style={{
                fontSize: "28px",
                fontFamily: "var(--font-serif)",
                fontWeight: 700,
                color: "#1B1B1A",
              }}
            >
              {s.value}
            </div>
            <div
              style={{
                fontSize: "11px",
                fontFamily: "var(--font-mono)",
                color: "#999999",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                marginTop: "2px",
              }}
            >
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Category breakdown */}
      {catData.length > 0 && (
        <div
          style={{
            backgroundColor: "#f7f3ea",
            borderRadius: "4px",
            border: "1px solid #999",
            padding: "20px",
          }}
        >
          <h3
            style={{
              margin: "0 0 16px",
              fontSize: "15px",
              fontFamily: "var(--font-serif)",
              fontWeight: 600,
            }}
          >
            Coverage by <em>Category</em>
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={catData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={90}
                innerRadius={45}
                paddingAngle={2}
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
                style={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
              >
                {catData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ fontSize: 12, fontFamily: "var(--font-mono)" }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Weekly volume */}
      {weekData.length > 1 && (
        <div
          style={{
            backgroundColor: "#f7f3ea",
            borderRadius: "4px",
            border: "1px solid #999",
            padding: "20px",
          }}
        >
          <h3
            style={{
              margin: "0 0 16px",
              fontSize: "15px",
              fontFamily: "var(--font-serif)",
              fontWeight: 600,
            }}
          >
            Weekly <em>Volume</em>
          </h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={weekData}>
              <XAxis
                dataKey="week"
                tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ fontSize: 12, fontFamily: "var(--font-mono)" }}
              />
              <Bar
                dataKey="count"
                fill={jurColor}
                radius={[3, 3, 0, 0]}
                name="Articles"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top sources */}
      {sourceData.length > 0 && (
        <div
          style={{
            backgroundColor: "#f7f3ea",
            borderRadius: "4px",
            border: "1px solid #999",
            padding: "20px",
          }}
        >
          <h3
            style={{
              margin: "0 0 14px",
              fontSize: "15px",
              fontFamily: "var(--font-serif)",
              fontWeight: 600,
            }}
          >
            Top <em>Sources</em>
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {sourceData.map(([source, count]) => (
              <div
                key={source}
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <div
                  style={{
                    flex: 1,
                    fontSize: "13px",
                    fontFamily: "var(--font-sans)",
                    color: "#1B1B1A",
                  }}
                >
                  {source}
                </div>
                <div
                  style={{
                    width: `${Math.min((count / sourceData[0][1]) * 200, 200)}px`,
                    height: "14px",
                    backgroundColor: `${jurColor}30`,
                    borderRadius: "2px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      right: "6px",
                      top: "0",
                      fontSize: "10px",
                      fontFamily: "var(--font-mono)",
                      color: jurColor,
                      lineHeight: "14px",
                      fontWeight: 600,
                    }}
                  >
                    {count}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {totalStored === 0 && (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#999999" }}>
          <p
            style={{
              fontFamily: "var(--font-serif)",
              fontSize: "16px",
              margin: "0 0 8px",
            }}
          >
            No data yet for {jurName}
          </p>
          <p style={{ fontFamily: "var(--font-sans)", fontSize: "13px" }}>
            Fetch news from the Feed tab to start building your database. Trends
            will appear here as articles for {jurName} accumulate.
          </p>
        </div>
      )}
    </div>
  );
}
